import React, {
  useEffect,
  useRef,
  useState,
  useContext,
  useMemo,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import ReactPannellum, {
  addHotSpot,
  getCurrentScene,
  loadScene,
  resize,
  lookAt,
  setPitchBounds,
  setHfovBounds,
  getViewer,
  getViewers,
} from "react-pannellum";
import { createRoot } from "react-dom/client";
import * as Tooltip from "react-tooltip";

import fullScreenMaximizeImage from "../../../assets/panorama/fullscreen-maximize.png";
import musicOnImage from "../../../assets/panorama/music-on.png";
import musicMuteImage from "../../../assets/panorama/music-mute.png";
import cycleDayImage from "../../../assets/panorama/cycle-day.png";
import cycleNightImage from "../../../assets/panorama/cycle-night.png";
import materialImage from "../../../assets/panorama/material.png";
import floorPlanImage from "../../../assets/panorama/floor-plan.png";
import { ReactComponent as LikeSvg } from "../../../assets/icons/like.svg";
// import { ReactComponent as LikeFilledSvg } from "../../../assets/icons/liked.svg";
import { ReactComponent as LikeFilledSvg } from "../../../assets/icons/liked-red.svg";
import { ReactComponent as ShareWhiteSvg } from "../../../assets/icons/share.svg";
import { ReactComponent as EyeSvg } from "../../../assets/icons/eye-white.svg";
import { ReactComponent as PanoramaIndicatorSvg } from "../../../assets/icons/360-indicator.svg";
import { ReactComponent as ImageIndicatorSvg } from "../../../assets/icons/image-indicator.svg";
import { ReactComponent as SwapSvg } from "../../../assets/panorama/swap.svg";

// * Hotspot Icons
import hotspotPinImage from "../../../assets/panorama/hotspot.gif";
import hotspotPin2Image from "../../../assets/panorama/icons/hotspot-3.gif";
import hotspotPin3Image from "../../../assets/panorama/icons/hotspot-4.gif";
import leftTurnImage from "../../../assets/panorama/icons/left.gif";
import rightTurnImage from "../../../assets/panorama/icons/right.gif";
import forwardTurnImage from "../../../assets/panorama/icons/forward.gif";
import leftTurn2Image from "../../../assets/panorama/icons/left-2.gif";
import rightTurn2Image from "../../../assets/panorama/icons/right-2.gif";
import voiceMemoImage from "../../../assets/panorama/icons/voice.gif";
import imagePinImage from "../../../assets/panorama/icons/image.gif";

import ProjectDescription from "../ProjectDescription";
import ContentLoader from "react-content-loader";
import AppContext from "../../../AppContext";
import { langComponent } from "../../../constants";
import { uuidv4 } from "../../../helpers/common.helper";
import PanoramaContext from "./Context";
import MaterialSwitcher from "./MaterialSwitcher";
import ToolTipInfo from "./ToolTipInfo";
import SwapScene from "./SwapScene";
import FloorPlan from "./FloorPlan";
import { useSwiper, useSwiperSlide } from "swiper/react";
import useScreenSize from "../../../hooks/useScreenSize";

const assetsURI = import.meta.env.ASSETS_URI;

const HOTSPOT_TYPE = {
  WALK_WAY: 1,
  INFO: 2,
  VOICE_MEMO: 3,
  PICTURE: 4,
};

var audioPlayer = null;

const Panorama = ({
  workId,
  panoramas: _panoramas,
  height,
  isPreview,
  onViewWork,
  autoRotate,
  isHaveMusic,
  isMusicPlaying,
  onToggleMusic,
  cycles = [],
  materials = [],
  floorPlans = [],
  work,
  draggable = true,
  fullScreen = false,
  isLiked: _liked,
  onLikeChanged,
  onShare,
  watermark,
  rating,
  ...props
}) => {
  const { getLang, addLikes, removeLikes, projectViewing } =
    useContext(AppContext);
  const [viewId] = useState(uuidv4());
  const [panoramas] = useState(_panoramas);
  const [selectedPanorama, setSelectedPanorama] = useState(panoramas[0]._id);
  const pannellumRef = useRef();
  const [isFullScreen, setIsFullScreen] = useState(fullScreen);
  const [isDisplayMaterialSwitcher, setIsDisplayMaterialSwitcher] =
    useState(false);
  const [isDisplayTooltipInfo, setIsDisplayTooltipInfo] = useState(undefined);
  const [isLiked, setIsLiked] = useState(_liked);
  const [isDisplaySwapScene, setIsDisplaySwapScene] = useState(false);
  const [isPanoramaLoaded, setIsPanoramaLoaded] = useState(false);
  const [isDisplayFloorPlan, setIsDisplayFloorPlan] = useState(false);
  const widgetRef = useRef(null);
  const swiper = useSwiper();
  const [isLoaded, setIsLoaded] = useState(
    swiper && props.index > 0 ? false : true
  );
  const { screenSize } = useScreenSize();
  const viewer = useRef(null);

  const _baseConfigs = {
    autoLoad: true,
    orientationOnByDefault: false,
    showZoomCtrl: true,
    keyboardZoom: false,
    disableKeyboardCtrl: true,
    showFullscreenCtrl: false,
    showControls: false,
    autoRotateInactivityDelay: 10000,
    yaw: 0,
    draggable,
    mouseZoom: isFullScreen,
    autoRotate: autoRotate ?? -5,
  };

  // * Mutate config
  const _config = !isPreview
    ? {
        ..._baseConfigs,
        // pitch: -180,
        minPitch: -180,
        maxHfov: 170,
        // hfov: 170,
        // autoRotate: 0,
      }
    : {
        ..._baseConfigs,
        // pitch: 0,
      };

  const containerRef = useCallback((node) => {
    if (node !== null) {
      const resizeObserver = new ResizeObserver((entries) => {
        resize();
      });
      resizeObserver.observe(node);

      // node.scrollIntoView({
      //   behavior: "instant",
      //   block: "start",
      // });
    }
  }, []);

  useEffect(() => {
    const handleSlideChangeTransitionEnd = () => {
      if (isLoaded) {
        return;
      }

      if (swiper.activeIndex !== props.index) {
        return;
      }

      setIsLoaded(true);
    };

    if (swiper) {
      swiper.on("slideChangeTransitionEnd", handleSlideChangeTransitionEnd);
    }

    return () => {
      if (swiper) {
        swiper.off("slideChangeTransitionEnd", handleSlideChangeTransitionEnd);
      }

      if (viewer.current && !isPreview) {
        viewer.current.destroy();
        viewer.current = null;
      }

      if (audioPlayer) {
        audioPlayer.pause();
        audioPlayer = null;
      }
    };
  }, []);

  useEffect(() => {
    setIsLiked(_liked);
  }, [_liked]);

  useEffect(() => {
    resize();
  }, [isFullScreen]);

  useEffect(() => {
    // * Get current scene
    const _currentScene = getCurrentScene();

    if (!_currentScene) return;
    if (_currentScene.sceneId === selectedPanorama) return;

    loadScene(selectedPanorama);
  }, [selectedPanorama]);

  // useEffect(() => {
  //   if (isDisplayMaterialSwitcher || isDisplayTooltipInfo)
  //     startAutoRotate(autoRotate === undefined ? -5 : autoRotate, 0);
  //   if (!isDisplayMaterialSwitcher && !isDisplayTooltipInfo)
  //     startAutoRotate(autoRotate === undefined ? -5 : autoRotate, 0);
  // }, [isDisplayMaterialSwitcher, isDisplayTooltipInfo]);

  /**
   * * Hotspot creation function
   * @param {*} hotSpotDiv
   * @param {*} param1
   */
  const createHotspot = (
    hotSpotDiv,
    { _id, title, desc, type, file, panorama, icon }
  ) => {
    const Element = () => {
      const getIconToRender = () => {
        if (type === HOTSPOT_TYPE.INFO) return hotspotPinImage;
        if (type === HOTSPOT_TYPE.PICTURE) return imagePinImage;
        if (type === HOTSPOT_TYPE.VOICE_MEMO) return voiceMemoImage;
        if (icon?.internal === -1) return icon.personal?.file;
        if (icon?.internal === 1) return forwardTurnImage;
        if (icon?.internal === 2) return leftTurnImage;
        if (icon?.internal === 3) return leftTurn2Image;
        if (icon?.internal === 4) return rightTurnImage;
        if (icon?.internal === 5) return rightTurn2Image;
        if (icon?.internal === 6) return hotspotPinImage;
        if (icon?.internal === 7) return hotspotPin2Image;
        if (icon?.internal === 8) return hotspotPin3Image;
      };

      const handleHotSpotClick = () => {
        if (type === HOTSPOT_TYPE.WALK_WAY) {
          setSelectedPanorama(panorama);
          return;
        }

        setIsDisplayTooltipInfo({
          title,
          desc,
          type,
          file,
        });
      };

      const className = ["hotspot-pin"];

      if (type === HOTSPOT_TYPE.WALK_WAY)
        className.push("hotspot-pin--walk-way");

      return (
        <div className={className.join(" ")} onClick={handleHotSpotClick}>
          <img className="hotspot-pin__icon" src={getIconToRender()} />
          <div className="hotspot-pin__tooltip">{title}</div>
        </div>
      );
    };

    const root = createRoot(hotSpotDiv);
    root.render(<Element />);
  };

  const _handlePanoramaLoaded = () => {
    if (!viewer.current) {
      for (const _viewer of getViewers()) {
        if (_viewer.getContainer().id !== viewId) {
          continue;
        }

        viewer.current = _viewer;
        break;
      }
    }

    if (!isPreview && !isPanoramaLoaded) {
      lookAt(-180, 0, 170, false);

      setTimeout(() => {
        lookAt(0, 0, 120, 1500, () => {
          setPitchBounds([-90, 90]);
          setHfovBounds([50, 120]);
        });
      }, 100);
    }

    if (isPanoramaLoaded) return true;

    setIsPanoramaLoaded(true);

    if (isPreview) return;

    for (let i = 0; i < panoramas.length; i++) {
      // * Add scene only for other images
      const panorama = panoramas[i];

      // * Only add hotspots for first panorama
      if (panorama._id === selectedPanorama) {
        const sceneId = panorama._id;

        for (const hotspot of panorama.hotspots) {
          const {
            _id,
            type,
            tooltip: { title, desc },
            pitch,
            yaw,
            panorama,
            icon,
            file,
          } = hotspot;

          addHotSpot(
            {
              id: _id,
              pitch,
              yaw,
              text: title,
              type: "custom",
              cssClass: "hotpot__pin",
              createTooltipFunc: createHotspot,
              createTooltipArgs: {
                id: _id,
                title,
                desc,
                panorama,
                type,
                icon,
                file: file?.file,
              },
            },
            sceneId
          );
        }

        continue;
      }

      const sceneId = panorama._id;

      viewer.current.addScene(
        sceneId,
        {
          ..._baseConfigs,
          imageSource: `${assetsURI}/${panorama.image}`,
          hotSpots: panorama.hotspots.map((hotspot) => {
            const {
              type,
              _id,
              tooltip: { title, desc },
              pitch,
              yaw,
              panorama,
              icon,
              file,
            } = hotspot;

            return {
              id: _id,
              pitch,
              yaw,
              text: title,
              type: "custom",
              cssClass: "hotpot__pin",
              createTooltipFunc: createHotspot,
              createTooltipArgs: {
                id: _id,
                title,
                desc,
                panorama,
                type,
                icon,
                file: file?.file,
              },
            };
          }),
        },
        () => {
          console.log("Scene added");
        }
      );
    }
  };

  const handleCloseOverlay = () => {
    setIsDisplayMaterialSwitcher(false);
    setIsDisplayTooltipInfo(false);
    setIsDisplaySwapScene(false);
    setIsDisplayFloorPlan(false);
  };

  const handleToggleFullScreen = () => {
    setIsFullScreen((curr) => !curr);
  };

  const renderCycleSwitcher = (cycle) => {
    const isDay = cycle.day === selectedPanorama;

    return (
      <div
        className="pannellum-controls-cycle-toggler"
        data-tooltip-id="my-tooltip"
        data-tooltip-content={`Switch to ${isDay ? "Night" : "Day"}`}
        data-tooltip-place="right"
        onClick={() => {
          setSelectedPanorama(isDay ? cycle.night : cycle.day);
        }}
      >
        <img
          className="pannellum-controls-cycle-toggler__icon"
          alt="icon"
          src={isDay ? cycleNightImage : cycleDayImage}
        />
      </div>
    );
  };

  const getDesc = () => {
    const {
      name,
      company,
      owner: { avatar, name: ownerName, plan },
      createdBy,
      program,
      renderer,
    } = work;

    return (
      <ProjectDescription
        userId={company ?? createdBy}
        company={company}
        avatar={avatar}
        workName={name}
        ownerName={ownerName}
        plan={plan}
        rating={rating}
        program={program}
        renderer={renderer}
      />
    );
  };

  const handleLikeClick = async () => {
    if (!isLiked) {
      if (!(await addLikes(workId))) {
        return;
      }

      setIsLiked(true);
      onLikeChanged && onLikeChanged(true);
      return;
    }

    await removeLikes(workId);
    setIsLiked(false);
    onLikeChanged && onLikeChanged(false);
  };

  const handleShareClick = () => {
    onShare && onShare();
  };

  const isDisplayLikeAndShare = () => {
    if (isPreview) {
      return true;
    }

    if (!isPreview && isFullScreen) {
      return true;
    }

    return false;
  };

  const contextValue = useMemo(
    () => ({
      panoramas,
      selectedPanorama,
      setSelectedPanorama,
      materials,
      tooltipInfo: isDisplayTooltipInfo,
      closeOverlay: handleCloseOverlay,
      floorPlans,
    }),
    [panoramas, selectedPanorama, materials, isDisplayTooltipInfo, floorPlans]
  );

  if (!viewId) {
    return <>Loading</>;
  }

  const { image, hotspots } =
    panoramas?.find((p) => p._id === selectedPanorama) ?? {};

  // * Check if there is materials
  const materialExists =
    materials &&
    materials.length > 0 &&
    materials.find((m) =>
      m.panoramas.find((p) => p.panorama === selectedPanorama)
    );

  // * Check if there is a cycle binded
  const cycle = cycles.find(
    (c) => c.day === selectedPanorama || c.night === selectedPanorama
  );

  if (!isLoaded) {
    return (
      <img
        src={work?.banner}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
        }}
      />
    );
  }

  const displayTooltip = screenSize !== "sm";

  return (
    <PanoramaContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        className={`pannellum ${isFullScreen ? "pannellum--fullscreen" : ""}`}
      >
        <ReactPannellum
          id={viewId}
          sceneId={panoramas[0]._id}
          ref={pannellumRef}
          onPanoramaLoaded={_handlePanoramaLoaded}
          imageSource={
            isPreview
              ? `${assetsURI}/${panoramas[0].compressed}`
              : `${assetsURI}/${panoramas[0].image}`
          }
          style={{
            height: "100%",
          }}
          config={{
            ..._config,
            preview: `${assetsURI}/${work?.banner}` ?? image,
            draggable: !isPreview,
          }}
        />
        <div className="pannellum-controls">
          <div className="pannellum-controls-left">
            <div className="pannellum-controls-views">
              <EyeSvg />
              <span>{work?.views?.toLocaleString() ?? 0}</span>
            </div>
            {isDisplayLikeAndShare() && (
              <>
                <div
                  className="pannellum-controls-like"
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content={"Like"}
                  onClick={handleLikeClick}
                >
                  {isLiked ? (
                    <LikeFilledSvg
                      className="pannellum-controls-like__icon"
                      style={{ marginTop: "3px" }}
                      width={24}
                    />
                  ) : (
                    <LikeSvg
                      className="pannellum-controls-like__icon"
                      style={{ marginTop: "3px" }}
                      width={24}
                    />
                  )}
                </div>
                <div
                  className="pannellum-controls-like"
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content={"Share"}
                  onClick={handleShareClick}
                >
                  <ShareWhiteSvg width="26" />
                </div>
              </>
            )}
            {/* {watermark && (
              <img
                className="pannellum-controls-watermark"
                alt="watermark"
                src={watermark}
              />
            )} */}
          </div>
          <div className="pannellum-controls-right">
            {!isPreview && (
              <div
                className={`pannellum-controls-toggle ${
                  isFullScreen ? "pannellum-controls-toggle--active" : ""
                }`}
                onClick={handleToggleFullScreen}
              >
                <img
                  className="pannellum-controls-toggle__icon"
                  src={fullScreenMaximizeImage}
                  alt="toggle"
                />
                <span>{!isFullScreen ? "Fullscreen" : "Exit"}</span>
              </div>
            )}
            {!isPreview && isPanoramaLoaded && (
              <>
                {isHaveMusic && (
                  <div
                    className={`pannellum-controls-material-toggler ${
                      isMusicPlaying === undefined
                        ? "pannellum-controls-material-toggler--disable"
                        : ""
                    }`}
                    onClick={() => {
                      onToggleMusic && onToggleMusic();
                    }}
                    data-tooltip-id="my-tooltip"
                    data-tooltip-content="Toggle Music"
                    data-tooltip-place="left"
                  >
                    <img
                      className="pannellum-controls-material-toggler__icon"
                      alt="icon"
                      src={isMusicPlaying ? musicOnImage : musicMuteImage}
                    />
                  </div>
                )}
                {materialExists && (
                  <div
                    className="pannellum-controls-material-toggler"
                    onClick={() => setIsDisplayMaterialSwitcher(true)}
                    data-tooltip-id="my-tooltip"
                    data-tooltip-content="Change material"
                    data-tooltip-place="right"
                  >
                    <img
                      className="pannellum-controls-material-toggler__icon"
                      alt="icon"
                      src={materialImage}
                    />
                  </div>
                )}
                {cycle && renderCycleSwitcher(cycle)}
                {panoramas.length > 1 && (
                  <div
                    className="pannellum-controls-material-toggler"
                    onClick={() => setIsDisplaySwapScene(true)}
                    data-tooltip-id="my-tooltip"
                    data-tooltip-content="Swap scene"
                    data-tooltip-place="right"
                  >
                    <SwapSvg width={28} />
                  </div>
                )}
                {floorPlans.length > 0 && (
                  <div
                    className="pannellum-controls-floor-plans-toggler"
                    onClick={() => setIsDisplayFloorPlan(true)}
                    data-tooltip-id="my-tooltip"
                    data-tooltip-content="Floor Plans"
                    data-tooltip-place="right"
                  >
                    <img
                      className="pannellum-controls-floor-plans-toggler__icon"
                      alt="icon"
                      src={floorPlanImage}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="pannellum-info">{isFullScreen && getDesc()}</div>
        <div className="pannellum-overlays">
          {isDisplayMaterialSwitcher && <MaterialSwitcher />}
          {isDisplayTooltipInfo && <ToolTipInfo />}
          {isDisplaySwapScene && <SwapScene />}
          {isDisplayFloorPlan && <FloorPlan />}
          {isPreview && (
            <div className="pannellum-view" onClick={onViewWork}>
              <PanoramaIndicatorSvg className="pannellum-view__icon" />
            </div>
          )}
        </div>
      </div>
      <Tooltip.Tooltip id={`my-tooltip${displayTooltip ? "" : "-hide"}`} />
    </PanoramaContext.Provider>
  );
};

Panorama.propTypes = {
  workId: PropTypes.string.isRequired,
  onViewWork: PropTypes.func,
};

Panorama.defaultProps = {
  onViewWork: () => {},
};

export default Panorama;
